import { Component, OnInit, inject, ChangeDetectionStrategy } from "@angular/core";
import {
    FormsModule,
    ReactiveFormsModule,
    UntypedFormBuilder,
    UntypedFormControl,
    UntypedFormGroup
} from "@angular/forms";
import { ButtonComponent } from "@components/button/button.component";
import { InputComponent } from "@components/input/input.component";
import { PageComponent } from "@components/page/page.component";
import { RadiobuttonGroupComponent } from "@components/radiobuttongroup/radiobuttongroup.component";
import { SearchComponent } from "@components/search/search.component";
import { SubmitButtonComponent } from "@components/submit-button/submit-button.component";
import { TogglePanelComponent } from "@components/toggle-panel/toggle-panel.component";
import { TranslatePipe } from "@ngx-translate/core";
import { BusyService } from "~app/common/components/busy/busy.service";
import { IRadiobuttonItem } from "~app/common/components/radiobuttongroup/radiobutton/IRadiobuttonItem";
import { ITypeAheadResult } from "~app/common/models/type-ahead-result.model";
import { StatedataService, Volatile } from "~app/common/services/statedata.service";
import { WebinstallningarService } from "~app/common/services/webinstallningar/webinstallningar.service";
import { KundListComponent } from "./components/kund-list/kund-list.component";
import { KontaktKundListResponseDTO } from "./models/kundList";
import { KundSearchParamsModel } from "./models/kundSearchParamsModel";
import { KundService } from "./services/kund.service";

@Component({
    selector: "rask-kund",
    templateUrl: "./kund.component.html",
    styleUrls: ["./kund.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        PageComponent,
        FormsModule,
        ReactiveFormsModule,
        SearchComponent,
        RadiobuttonGroupComponent,
        TogglePanelComponent,
        InputComponent,
        SubmitButtonComponent,
        ButtonComponent,
        KundListComponent,
        TranslatePipe
    ]
})
export class KundComponent implements OnInit {
    active = new UntypedFormControl("true");
    aktivButtons = [
        { key: true,
            value: "Ja" },
        { key: false,
            value: "Nej" },
        { key: null,
            value: "Alla" }
    ] as IRadiobuttonItem[];
    befraktare = new UntypedFormControl("true");
    befraktareButtons = [
        { key: true,
            value: "Ja" },
        { key: false,
            value: "Nej" },
        { key: null,
            value: "Alla" }
    ] as IRadiobuttonItem[];
    form: UntypedFormGroup;
    kunder: KontaktKundListResponseDTO;
    kunderSearchParams: KundSearchParamsModel;
    volatile: Volatile;

    private readonly WIK = "Kund.searchparams";
    private readonly busyService = inject(BusyService);
    private readonly formBuilder = inject(UntypedFormBuilder);
    private readonly kundService = inject(KundService);
    private readonly stateDataService = inject(StatedataService);
    private readonly webinstallningarService = inject(WebinstallningarService);

    constructor() {
        this.volatile = this.stateDataService.getVolatile();
        this.kunderSearchParams = {};
    }

    formatter = (result: ITypeAheadResult) => result ? result.namn : undefined;

    mapFromForm(): KundSearchParamsModel {
        return {
            aktiv: this.form.get("aktiv").value,
            beskrivning: this.form.get("beskrivning").value,
            sokTypeAhead: this.form.get("sokTypeAhead").value,
            id: null,
            namn: this.form.get("namn").value,
            orgNummer: this.form.get("orgNummer").value,
            besokPostortadress: this.form.get("besokPostortadress").value,
            huvudkod: this.form.get("huvudkod").value,
            internnummer: this.form.get("internnummer").value,
            limeId: this.form.get("limeId").value,
            befraktare: this.form.get("befraktare").value
        };
    }

    ngOnInit() {
        this.kunderSearchParams = this.volatile.get<KundSearchParamsModel>(this.WIK);
        if (this.kunderSearchParams) {
            this.form = this.formBuilder.group(this.kunderSearchParams);
            this.form.addControl("active", new UntypedFormControl(this.parseFalsyBool(this.kunderSearchParams.aktiv)));
            this.form.addControl("befraktare", new UntypedFormControl(this.parseFalsyBool(this.kunderSearchParams.befraktare)));
            this.search(false);
            return;
        }

        this.resetToWebinstallning();
    }

    outputFormatter = (result: ITypeAheadResult) => result ? [result] : undefined;

    reset() {
        this.form.get("sokTypeAhead").setValue(null);
        this.form.get("aktiv").setValue(null);
        this.form.get("id").setValue(null);
        this.form.get("namn").setValue(null);
        this.form.get("orgNummer").setValue(null);
        this.form.get("besokPostortadress").setValue(null);
        this.form.get("huvudkod").setValue(null);
        this.form.get("internnummer").setValue(null);
        this.form.get("limeId").setValue(null);
        this.form.get("beskrivning").setValue(null);
        this.form.get("befraktare").setValue(null);
        this.kunder = null;
    }

    resetToWebinstallning() {
        let searchParams = this.webinstallningarService.get<KundSearchParamsModel>(this.WIK);
        if (searchParams == null) {
            searchParams = new KundSearchParamsModel();
        }
        this.kunderSearchParams = searchParams;
        this.form = this.formBuilder.group(this.kunderSearchParams);
        this.form.addControl("active", new UntypedFormControl(this.parseFalsyBool(searchParams.aktiv)));
        this.form.addControl("befraktare", new UntypedFormControl(this.parseFalsyBool(searchParams.befraktare)));
    }

    search(saveState = true) {
        const params = this.mapFromForm();
        const query = this.kundService.searchForKund(this.form.value);
        this.busyService.addWork(query).subscribe((kunder: KontaktKundListResponseDTO) => {
            this.kunder = kunder;
        });

        if (saveState) {
            this.webinstallningarService.set<KundSearchParamsModel>(this.WIK, params);
            this.volatile.set(this.WIK, params);
        }
    }

    searchKundTypeAhead = (searchString: string) => this.kundService.searchForKundTypeAhead(searchString);

    private parseFalsyBool(val: any) {
        if (val == null) {
            return "";
        }
        return val ? "Ja" : "Nej";
    }
}
